import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useServices } from '../providers/ServicesProvider';
import { useSubscription } from '../providers/SubscriptionProvider';
import { useAuth, signOut as authSignOut } from './AuthView';
import { REMOTE_ACCESS_ENABLED } from '../config/featureFlags';
import { CLOUD_API_URL } from '../config/serverConfig';
import { fetchCloudAuthenticated } from '../services/api';
import { billingService } from '../services/subscriptionService';
import { shouldRouteManageToCheckout } from '../utils/billingEntitlement';
import {
  buildCancellingEntitlementRows,
  formatBillingPlanLabel,
  formatSubscriptionPriceLine,
  getActiveRenewalDateIso,
  getCancelAccessEndDateIso,
  resolveDisplayedBillingInterval,
} from '../utils/subscriptionPanelDisplay';
import { shouldIgnoreAlreadySubscribed, shouldShowAlreadySubscribed } from '../utils/billingEntitlement';
import { canShowDeveloperSettings } from '../utils/devSettingsAccess';
import { getCancelConfirmationCopy } from '../services/cancelConfirmationCopy';
import { QobuzIcon, EllaRadioIcon, GramophoneIcon } from './ServiceIcons';
import EllaRadioBansSettings from './radio/EllaRadioBansSettings';
import useAppNavigation from '../hooks/useAppNavigation';
import FullFeaturedModal from './FullFeaturedModal';
import { useDesktopMode, useMaestroStore } from '../store/maestroStore';
import { LocationCard } from './LocationManager';
import { useQobuzStore, cancelOAuthLogin, shouldShowQobuzConnectPrompt } from '../lib/useQobuz';
import { useTheme } from '../lib/useTheme';
import { useTranslation } from '../i18n/useTranslation';
import LANGUAGES from '../i18n/languages';
import QobuzPlaylistRefreshPanel from './settings/QobuzPlaylistRefreshPanel';
import { useQobuzPlaylistRefresh } from '../hooks/useQobuzPlaylistRefresh';
import { getTauriDesktopOs, isIosShell } from '../utils/platform';
import IosSubscriptionManageHint from './IosSubscriptionManageHint';
import PrimaryActionError from './PrimaryActionError';
import { runPrimaryAction } from '../utils/primaryAction';
import {
  HOME_HOST_NEUTRAL,
  backgroundServiceOnHost,
  browseLocalOnHost,
  homeHostPhrase,
  hostOnlineLabel,
  libraryOnHost,
  allowLocalNetworkAccessLabel,
  scanLibraryOnHost,
  thisHostPhrase,
  wakeHostPhrase,
} from '../utils/desktopHostCopy';


const GOLD = '#C9A84C';

const SERVICES_LIST = [
  { id: 'qobuz', label: 'Qobuz', icon: <QobuzIcon />, connectable: true },
  { id: 'live_radio', label: 'Live Radio', icon: <span style={{ fontSize: 20 }}>📡</span>, noAuth: true },
  { id: 'ella_radio', label: 'Ella Radio', icon: <EllaRadioIcon />, noAuth: true },
  { id: 'octave_radio', label: 'Octave Radio', icon: <GramophoneIcon />, noAuth: true },
];

function PillToggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onChange(!checked);
      }}
      style={{
        width: 48, height: 26, borderRadius: 13, position: 'relative',
        background: checked ? '#22c55e' : 'var(--border-light, #2c2c2e)',
        transition: 'background 0.2s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        flexShrink: 0,
        zIndex: 2,
      }}
    >
      <span
        style={{
          position: 'absolute', top: 3, width: 20, height: 20,
          borderRadius: '50%', background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          transition: 'left 0.2s ease',
          left: checked ? 25 : 3,
        }}
      />
    </button>
  );
}

function SectionHeader({ children }) {
  return (
    <h2 style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: 'var(--text-tertiary)',
      marginBottom: 12, marginTop: 28,
    }}>
      {children}
    </h2>
  );
}

const CHANGELOG_SEEN_KEY = 'maestro_changelog_last_seen';

function WhatsNewSection() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null); // index of expanded entry
  const [hasNew, setHasNew] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/changelog`);
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        const list = data?.entries || [];
        setEntries(list);
        setExpanded(0);
        const lastSeen = localStorage.getItem(CHANGELOG_SEEN_KEY);
        if (list.length > 0 && list[0].version !== lastSeen) {
          setHasNew(true);
        }
      } catch {
        setError(true);
        setEntries([]);
      }
    })();
  }, []);

  const handleToggle = () => {
    setOpened(!opened);
    if (!opened && hasNew && entries?.length > 0) {
      localStorage.setItem(CHANGELOG_SEEN_KEY, entries[0].version);
      setHasNew(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <>
      <SectionHeader>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          What&apos;s New
          {hasNew && (
            <span style={{
              width: 7, height: 7, borderRadius: '50%', background: GOLD,
              display: 'inline-block', flexShrink: 0,
            }} />
          )}
        </span>
      </SectionHeader>
      <button
        type="button"
        onClick={handleToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '14px', borderRadius: 14,
          background: 'var(--bg-card)', border: '1px solid var(--border-light)',
          cursor: 'pointer', transition: 'background 0.15s',
          textAlign: 'left', font: 'inherit', color: 'inherit', boxSizing: 'border-box',
        }}
      >
        <span style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'rgba(201,168,76,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Release Notes</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
            See what changed in recent updates
          </div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"
          style={{ transform: opened ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>

      {opened && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {entries === null && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-tertiary)' }}>Loading…</div>
          )}
          {error && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-tertiary)' }}>
              No release notes available yet.
            </div>
          )}
          {entries && entries.length === 0 && !error && (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-tertiary)' }}>
              No release notes available yet.
            </div>
          )}
          {entries && entries.map((entry, i) => (
            <div key={entry.version} style={{
              borderRadius: 12, overflow: 'hidden',
              border: '1px solid var(--border-light)',
              background: 'var(--bg-card)',
            }}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === i ? null : i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', cursor: 'pointer',
                  background: 'transparent', border: 'none',
                  font: 'inherit', color: 'inherit', textAlign: 'left',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2.5"
                  style={{ transform: expanded === i ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                  <path d="M9 18l6-6-6-6"/>
                </svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    v{entry.version}
                  </span>
                  {entry.title && (
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 8 }}>
                      — {entry.title}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', flexShrink: 0 }}>
                  {formatDate(entry.released)}
                </span>
              </button>
              {expanded === i && entry.highlights && (
                <ul style={{
                  margin: 0, padding: '0 14px 12px 40px',
                  listStyle: 'disc',
                }}>
                  {entry.highlights.map((h, hi) => (
                    <li key={hi} style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 4 }}>
                      {h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function PublicNameSection() {
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCloudAuthenticated('/api/auth/display-name')
      .then(r => { setName(r?.display_name || ''); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await fetchCloudAuthenticated('/api/auth/display-name', {
        method: 'PATCH',
        body: JSON.stringify({ display_name: trimmed }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('[Settings] Failed to save display name:', e);
    }
  };

  return (
    <div style={{
      marginTop: 16, padding: 16, borderRadius: 14,
      background: 'var(--bg-card)',
      border: '1px solid var(--border-light)',
    }}>
      <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>Public Name</div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10 }}>
        This name is shown when you share playlists or stations with the Community.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={loading ? 'Loading...' : 'Enter a nickname'}
          maxLength={50}
          style={{
            flex: 1, fontSize: 14, padding: '9px 12px', borderRadius: 8,
            background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
          }}
        />
        <button
          onClick={handleSave}
          disabled={!name.trim()}
          style={{
            padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: saved ? '#4ade80' : GOLD, color: '#1a1a2e',
            cursor: name.trim() ? 'pointer' : 'not-allowed', opacity: name.trim() ? 1 : 0.5,
            transition: 'all 0.15s', border: 'none',
          }}
        >
          {saved ? 'Saved!' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordSection({ auth }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPw || !newPw || !confirmPw) {
      setError('All fields are required');
      return;
    }
    if (newPw.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPw !== confirmPw) {
      setError('Passwords don\'t match');
      return;
    }

    setLoading(true);
    try {
      const email = auth?.user?.email;
      if (!email) { setError('No email found'); setLoading(false); return; }

      const signInRes = await fetch(`${CLOUD_API_URL}/api/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: currentPw }),
      });
      if (!signInRes.ok) {
        setError('Current password is incorrect');
        setLoading(false);
        return;
      }

      const token = auth?.accessToken;
      const updateRes = await fetch(`${CLOUD_API_URL}/api/auth/update-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: newPw }),
      });
      const updateData = await updateRes.json().catch(() => ({}));
      if (!updateRes.ok) {
        setError(updateData?.error || 'Failed to update password');
      } else {
        setSuccess('Password updated successfully');
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', fontSize: 13, padding: '9px 36px 9px 12px', borderRadius: 8,
    background: 'var(--bg-secondary)', border: '1px solid var(--border-light)',
    color: 'var(--text-primary)', outline: 'none',
  };
  const eyeBtn = {
    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', padding: 4,
    color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center',
  };

  const EyeIcon = ({ open }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {open ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      )}
    </svg>
  );

  return (
    <div style={{
      marginTop: 16, padding: 16, borderRadius: 14,
      background: 'var(--bg-card)', border: '1px solid var(--border-light)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>
        Change Password
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ position: 'relative' }}>
          <input
            type={showCurrent ? 'text' : 'password'}
            placeholder="Current password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
            style={inputStyle}
          />
          <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={eyeBtn}>
            <EyeIcon open={showCurrent} />
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showNew ? 'text' : 'password'}
            placeholder="New password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
          <button type="button" onClick={() => setShowNew(!showNew)} style={eyeBtn}>
            <EyeIcon open={showNew} />
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showConfirm ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={eyeBtn}>
            <EyeIcon open={showConfirm} />
          </button>
        </div>

        {error && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 2 }}>{error}</div>}
        {success && <div style={{ fontSize: 12, color: '#22c55e', marginTop: 2 }}>{success}</div>}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
            background: loading ? 'var(--bg-secondary)' : 'rgba(201,168,76,0.1)',
            color: loading ? 'var(--text-tertiary)' : GOLD,
            cursor: loading ? 'wait' : 'pointer', transition: 'background 0.15s',
            marginTop: 4,
          }}
        >
          {loading ? 'Updating…' : 'Update Password'}
        </button>
      </form>
    </div>
  );
}

function RemoteLibraryViewerNote() {
  return (
    <div style={{
      marginTop: 8, padding: 14, borderRadius: 14,
      background: 'var(--bg-card)', border: '1px solid var(--border-light)',
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
        Remote library viewer
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6, lineHeight: 1.5 }}>
        This device streams your home library over a private connection. It does not host your music files.
        To install the background service or change remote access, open Settings on the Mac where your library lives.
      </p>
    </div>
  );
}

function RemoteAccessToggle({ onInstallStateChange }) {
  const auth = useAuth();
  const [enabled, setEnabled] = useState(
    () => localStorage.getItem('maestro_remote_access') !== '0',
  );
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const applyRemoteAccess = useCallback(async (turnOn) => {
    console.log('[Settings] remote access toggle clicked:', turnOn ? 'ON' : 'OFF');
    setStatus(null);
    setBusy(true);
    onInstallStateChange?.(turnOn ? 'installing' : 'idle');
    try {
      if (turnOn) {
        localStorage.removeItem('maestro_remote_access');
        localStorage.setItem('maestro_daemon_install_requested', Date.now().toString());
        if (typeof window !== 'undefined' && '__TAURI__' in window) {
          const connector = await import('../lib/connector');
          const { resolveRefreshTokenForDaemon } = await import('./AuthView');
          const creds = await resolveRefreshTokenForDaemon();
          await connector.enableHomeLibraryRemoteAccess({
            accessToken: creds.accessToken || auth?.accessToken,
            refreshToken: creds.refreshToken,
            userId: creds.userId || auth?.user?.id,
            email: creds.email || auth?.user?.email,
          });
          if (!creds.refreshToken) {
            setStatus('Service installed. Sign out and sign back in once so it can stay connected after you quit Maestro.');
          } else {
            setStatus('Background service installed — your library stays available when Maestro is closed.');
          }
          onInstallStateChange?.('installed');
        } else {
          setStatus('Remote access enabled.');
          onInstallStateChange?.('installed');
        }
        setEnabled(true);
      } else {
        localStorage.setItem('maestro_remote_access', '0');
        localStorage.removeItem('maestro_daemon_install_requested');
        if (typeof window !== 'undefined' && '__TAURI__' in window) {
          const connector = await import('../lib/connector');
          await connector.shutdownRemoteAccess();
        }
        setEnabled(false);
        setStatus('Remote library access turned off.');
        onInstallStateChange?.('idle');
      }
      window.dispatchEvent(new CustomEvent('maestro:remote-access-changed'));
    } catch (e) {
      console.error('[Settings] remote access toggle:', e);
      setEnabled(localStorage.getItem('maestro_remote_access') !== '0');
      setStatus(e?.message || 'Could not install background service. Use the button below to retry.');
      onInstallStateChange?.('failed');
    } finally {
      setBusy(false);
    }
  }, [auth?.accessToken, auth?.user?.id, auth?.user?.email, onInstallStateChange]);

  return (
    <>
      <div
        role="switch"
        aria-checked={enabled}
        onClick={() => !busy && applyRemoteAccess(!enabled)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px', borderRadius: 14, background: 'var(--bg-card)',
          border: '1px solid var(--border-light)', marginTop: 8,
          cursor: busy ? 'wait' : 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, marginRight: 16, pointerEvents: 'none' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Access my library from anywhere</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
            {backgroundServiceOnHost()}
          </div>
        </div>
        <PillToggle checked={enabled} onChange={applyRemoteAccess} disabled={busy} />
      </div>
      {busy && (
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8, paddingLeft: 4 }}>
          Installing background service…
        </p>
      )}
      {status && !busy && (
        <p style={{
          fontSize: 12,
          marginTop: 8,
          paddingLeft: 4,
          lineHeight: 1.5,
          color: status.includes('Could not') || status.includes('Sign out') ? '#f59e0b' : GOLD,
        }}>
          {status}
        </p>
      )}
    </>
  );
}

/** Visible on every device — shows whether the home library is reachable via tunnel. */
function HomeLibraryConnectionSection() {
  const [health, setHealth] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [enableBusy, setEnableBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);
  const isHomeLibraryHost = useMaestroStore((s) => s.isHomeLibraryHost);
  const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;
  const hostOs = getTauriDesktopOs();

  const fetchHealth = React.useCallback(async () => {
    try {
      const { libraryService } = await import('../services/libraryService');
      const h = await libraryService.getTunnelHealth();
      setHealth(h);
      const online = h?.ok && (h?.machines || []).some((m) => m.reachable && m.catalog_ready !== false);
      useMaestroStore.getState().setTunnelConnected(!!online);
    } catch {
      setHealth({ ok: false, machines: [], message: 'Could not reach home library' });
      useMaestroStore.getState().setTunnelConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 20000);
    const onChanged = () => { fetchHealth(); };
    window.addEventListener('maestro:remote-access-changed', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('maestro:remote-access-changed', onChanged);
    };
  }, [fetchHealth]);

  if (loading) {
    return (
      <div style={{ marginTop: 8, padding: 14, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
        <div style={{ width: 200, height: 14, borderRadius: 6, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
    );
  }

  const machines = health?.machines || [];
  const online = health?.ok && machines.some((m) => m.reachable && m.catalog_ready !== false);
  const registered = machines.length > 0;
  const reachableButNotReady = machines.some((m) => m.reachable && m.catalog_ready === false);

  let statusColor = '#ef4444';
  let statusText = 'Home library offline';
  let detail = isHomeLibraryHost
    ? 'Install the background service below — it keeps your library available when Maestro is closed.'
    : `Your ${HOME_HOST_NEUTRAL} runs a small background service that keeps your library available. Connection status is shown here — setup is done on the ${HOME_HOST_NEUTRAL} only.`;

  if (online) {
    statusColor = GOLD;
    statusText = 'Secured · Home library connected';
    detail = isHomeLibraryHost
      ? 'Your library is available here and on your other devices.'
      : 'Your home library is reachable from this device.';
  } else if (registered && reachableButNotReady) {
    statusColor = '#f59e0b';
    statusText = isHomeLibraryHost
      ? hostOnlineLabel(hostOs)
      : 'Home library online — catalog not ready';
    detail = `The connection is live but the catalog is still loading. Try again in a minute or rescan on your ${isHomeLibraryHost ? homeHostPhrase(hostOs) : HOME_HOST_NEUTRAL}.`;
  } else if (registered) {
    statusColor = '#f59e0b';
    statusText = 'Home library unreachable';
    detail = isHomeLibraryHost
      ? `The background service may have stopped. Use the button below to restart it, or ${wakeHostPhrase(hostOs)} — the service starts automatically at login.`
      : `Your ${HOME_HOST_NEUTRAL} may be asleep or the background service stopped. Wake the ${HOME_HOST_NEUTRAL} and check Settings there.`;
  } else if (health?.message === 'No tunnel registered for this account') {
    statusText = 'No home library registered';
    detail = isHomeLibraryHost
      ? `${scanLibraryOnHost(hostOs)}, then enable “Access my library from anywhere” below.`
      : `No ${HOME_HOST_NEUTRAL} has registered yet. On the computer where your music lives, open Maestro, scan your library, and enable remote access in Settings.`;
  }

  return (
    <div style={{ marginTop: 8, padding: 14, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: statusColor, fontWeight: 600 }}>{statusText}</span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '8px 0 0', paddingLeft: 18 }}>
        {detail}
      </p>
      {!online && isHomeLibraryHost && isTauri && (
        <>
          <button
            type="button"
            disabled={enableBusy}
            onClick={async () => {
              setEnableBusy(true);
              setActionError(null);
              try {
                const connector = await import('../lib/connector');
                const { resolveRefreshTokenForDaemon } = await import('./AuthView');
                localStorage.removeItem('maestro_remote_access');
                const creds = await resolveRefreshTokenForDaemon();
                await connector.enableHomeLibraryRemoteAccess(creds);
                await connector.registerLiveTunnelWithCloud(30000).catch(() => {});
                await fetchHealth();
              } catch (e) {
                const msg = e?.message || 'Could not enable home library access';
                setActionError(msg);
                console.error('[Settings] enable remote access:', e);
              }
              setEnableBusy(false);
            }}
            style={{
              marginTop: 10, marginLeft: 18, fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 8,
              background: GOLD, color: '#1a1a2e', cursor: enableBusy ? 'wait' : 'pointer',
              opacity: enableBusy ? 0.7 : 1,
            }}
          >
            {enableBusy ? 'Enabling…' : 'Enable home library access'}
          </button>
          {actionError && (
            <p style={{ fontSize: 11, color: '#ef4444', lineHeight: 1.5, margin: '8px 0 0 18px' }}>
              {actionError}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function DaemonStatusSection({ remoteInstallState = 'idle' }) {
  const isHomeLibraryHost = useMaestroStore((s) => s.isHomeLibraryHost);
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState(null);
  const [hasLibraryTracks, setHasLibraryTracks] = React.useState(false);
  const isWindows = typeof navigator !== 'undefined' && /Win/i.test(navigator.platform);
  const hostOs = getTauriDesktopOs();
  const tunnelConnected = useMaestroStore((s) => s.tunnelConnected);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { libraryService, probeLocalLibraryIpc } = await import('../services/libraryService');
        await probeLocalLibraryIpc();
        const stats = await libraryService.getStats();
        if (!cancelled) {
          const trackCount = stats?.total_tracks ?? stats?.tracks ?? 0;
          const albumCount = stats?.total_albums ?? stats?.albums ?? 0;
          setHasLibraryTracks(trackCount > 0 || albumCount > 0);
        }
      } catch { /* library stats unavailable */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchStatus = React.useCallback(async () => {
    const isTauri = typeof window !== 'undefined' && '__TAURI__' in window;

    // Step 1: Try Tauri IPC for detailed daemon status
    if (isTauri) {
      try {
        const connector = await import('../lib/connector');
        let result;
        try {
          result = await connector.verifyDaemonInstallation();
        } catch (verifyErr) {
          try {
            const basic = await connector.getDaemonStatus();
            result = {
              plist_exists: basic.installed,
              daemon_running: basic.running,
              tunnel_url_registered: !!basic.tunnel_url,
              error: null,
            };
          } catch {
            const msg = typeof verifyErr === 'string' ? verifyErr : verifyErr?.message || 'Failed to query daemon status';
            setStatus({ plist_exists: false, daemon_running: false, tunnel_url_registered: false, error: msg });
            return;
          }
        }

        // When the daemon manages the tunnel, the Tauri app's local tunnel_url is null.
        // Check Mycelium to see if the daemon registered its tunnel there.
        if (result && !result.tunnel_url_registered) {
          try {
            const resp = await fetchCloudAuthenticated('/api/library/tunnel', { method: 'GET' });
            if (resp.ok) {
              const data = await resp.json();
              if (data?.tunnel_url) {
                result = { ...result, tunnel_url_registered: true };
              }
            }
          } catch { /* Mycelium unreachable */ }
        }

        setStatus(result);
        useMaestroStore.getState().setTunnelConnected(!!result.tunnel_url_registered);
        setLoading(false);
        return;
      } catch (err) {
        const msg = typeof err === 'string' ? err : err?.message || 'Failed to query daemon status';
        setStatus({ plist_exists: false, daemon_running: false, tunnel_url_registered: false, error: msg });
        setLoading(false);
        return;
      }
    }

    // Step 2: Non-Tauri (browser) — check Mycelium directly
    try {
      const resp = await fetchCloudAuthenticated('/api/library/tunnel', { method: 'GET' });
      if (resp.ok) {
        const data = await resp.json();
        const hasTunnel = !!data?.tunnel_url;
        setStatus({
          plist_exists: hasTunnel,
          daemon_running: hasTunnel,
          tunnel_url_registered: hasTunnel,
          error: null,
        });
        useMaestroStore.getState().setTunnelConnected(hasTunnel);
      }
    } catch {
      // No tunnel info available in browser — leave status null
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    fetchStatus();
    const stored = localStorage.getItem('maestro_auth');
    const token = stored ? (() => { try { return JSON.parse(stored).accessToken; } catch { return null; } })() : null;
    if (token && typeof window !== 'undefined' && '__TAURI__' in window) {
      import('../lib/connector').then(c => c.setAuthToken(token)).catch(() => {});
    }
    const interval = setInterval(fetchStatus, 15000);
    const onChanged = () => { fetchStatus(); };
    window.addEventListener('maestro:remote-access-changed', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('maestro:remote-access-changed', onChanged);
    };
  }, [fetchStatus]);

  const handleInstallService = React.useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const connector = await import('../lib/connector');
      await connector.enableHomeLibraryRemoteAccess();
      await new Promise(r => setTimeout(r, 2500));
      await fetchStatus();
    } catch (e) {
      setActionError(e?.message || 'Install failed');
      console.error('[Settings] install background service:', e);
    }
    setActionLoading(false);
  }, [fetchStatus]);

  const handleRestart = React.useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const connector = await import('../lib/connector');
      await connector.stopDaemon();
      await new Promise(r => setTimeout(r, 1000));
      await connector.startDaemon();
      await new Promise(r => setTimeout(r, 2000));
      await fetchStatus();
    } catch (e) {
      setActionError(e?.message || 'Restart failed');
      console.error('[Settings] restart background service:', e);
    }
    setActionLoading(false);
  }, [fetchStatus]);

  const handleReinstall = React.useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const connector = await import('../lib/connector');
      try { await connector.uninstallDaemon(); } catch { /* may not be installed */ }
      await new Promise(r => setTimeout(r, 1000));
      await connector.enableHomeLibraryRemoteAccess();
      await new Promise(r => setTimeout(r, 2500));
      await fetchStatus();
    } catch (e) {
      setActionError(e?.message || 'Reinstall failed');
      console.error('[Settings] reinstall background service:', e);
    }
    setActionLoading(false);
  }, [fetchStatus]);

  if (!isHomeLibraryHost) return null;

  if (loading) {
    return (
      <div style={{ marginTop: 8, padding: 14, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
        <div style={{ width: 180, height: 14, borderRadius: 6, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </div>
    );
  }

  if (!status) return null;

  // End-to-end library reachability lives in HomeLibraryConnectionSection (tunnelConnected).
  // Here we only report background service install/run state.
  const serviceRunning = status.plist_exists && status.daemon_running;
  const inAppTunnel = !!status.in_app_tunnel || (!status.daemon_running && status.tunnel_url_registered);
  const allGood = serviceRunning || (inAppTunnel && tunnelConnected);
  const runningButConnecting = inAppTunnel && !tunnelConnected;
  const notRunning = status.plist_exists && !status.daemon_running && !inAppTunnel;
  const notInstalled = !status.plist_exists;
  const hasError = !!status.error;
  const installRequested = !!localStorage.getItem('maestro_daemon_install_requested');
  const hideNotInstalledPrompt =
    remoteInstallState === 'installing' ||
    remoteInstallState === 'installed' ||
    (installRequested && notInstalled && !hasError);

  return (
    <div style={{ marginTop: 8, padding: 14, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
      {hideNotInstalledPrompt && notInstalled && !hasError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
            {remoteInstallState === 'installing' ? 'Installing background service…' : 'Finishing setup…'}
          </span>
        </div>
      )}
      {allGood && !hasError && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: GOLD, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: GOLD, fontWeight: 600 }}>
              {serviceRunning ? 'Background service running' : 'Home library connected (in-app tunnel)'}
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '6px 0 0', paddingLeft: 18 }}>
            {serviceRunning
              ? (tunnelConnected
                ? (isWindows
                  ? 'Your library stays available when Maestro is closed (starts when you sign in to Windows).'
                  : 'Your library stays available when Maestro is closed.')
                : 'Service is running — waiting for home library connection (see status above).')
              : `Your library is reachable from other devices while Maestro stays open on ${thisHostPhrase(hostOs)}. Use Reinstall Service below to enable background access when the app is closed.`}
          </p>
          {status.update_available && status.update_version && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingLeft: 18 }}>
              <span style={{ fontSize: 13 }}>🔄</span>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                Daemon update available (v{status.update_version}) — installing automatically
              </span>
            </div>
          )}
        </div>
      )}

      {runningButConnecting && !hasError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', flexShrink: 0, animation: 'pulse 1.5s ease-in-out infinite' }} />
          <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
            Connecting to home library…
          </span>
        </div>
      )}

      {notRunning && !hasError && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
              Background service not running
            </span>
          </div>
          <button
            onClick={handleRestart}
            disabled={actionLoading}
            style={{
              width: '100%', fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
              background: GOLD, color: '#1a1a2e', cursor: actionLoading ? 'wait' : 'pointer',
              opacity: actionLoading ? 0.6 : 1,
            }}
          >
            {actionLoading ? 'Restarting…' : 'Restart Service'}
          </button>
        </>
      )}

      {notInstalled && !hasError && hasLibraryTracks && !hideNotInstalledPrompt && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>
              Background service not installed
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '0 0 10px' }}>
            {libraryOnHost(hostOs)}, but the background service that keeps it available remotely is not running yet. Install it once — Maestro does not need to stay open.
          </p>
          <button
            onClick={handleInstallService}
            disabled={actionLoading}
            style={{
              width: '100%', fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
              background: GOLD, color: '#1a1a2e', cursor: actionLoading ? 'wait' : 'pointer',
              opacity: actionLoading ? 0.6 : 1,
            }}
          >
            {actionLoading ? 'Installing…' : 'Install Background Service'}
          </button>
        </>
      )}

      {actionError && (
        <p style={{ fontSize: 11, color: '#ef4444', lineHeight: 1.5, margin: '0 0 10px', wordBreak: 'break-word' }}>
          {actionError}
        </p>
      )}

      {hasError && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 14 }}>✗</span>
            <span style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>
              Service error
            </span>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '0 0 10px', wordBreak: 'break-word' }}>
            {status.error}
          </p>
          <button
            onClick={handleReinstall}
            disabled={actionLoading}
            style={{
              width: '100%', fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
              background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
              cursor: actionLoading ? 'wait' : 'pointer', opacity: actionLoading ? 0.6 : 1,
            }}
          >
            {actionLoading ? 'Reinstalling…' : 'Reinstall Service'}
          </button>
        </>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return null; }
}

function tierLabel(tier, trialDays) {
  switch (tier) {
    case 'active': return { text: 'Active', color: '#22c55e' };
    case 'trial': return { text: trialDays != null ? `Trial · ${trialDays} days left` : 'Trial', color: GOLD };
    case 'cancelling': return { text: 'Cancelling', color: '#f59e0b' };
    case 'grace': return { text: 'Payment Issue', color: '#f59e0b' };
    case 'beta': return { text: 'Complimentary', color: '#8b5cf6' };
    case 'expired': return { text: 'Expired', color: '#ef4444' };
    default: return { text: 'Inactive', color: '#ef4444' };
  }
}

function DetailRow({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ color: color || 'var(--text-secondary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function QobuzPanel({ qobuzConnected, qobuzUser, qobuzDisconnect, qobuzLogin, qobuzLoginOAuth, qobuzConnecting, qobuzError, isQobuzActive }) {
  const { t } = useTranslation();
  const qobuzLinkResolved = useQobuzStore((s) => s.linkResolved);
  const [showLegacyLogin, setShowLegacyLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [connectingElapsed, setConnectingElapsed] = useState(0);
  const connectingTimerRef = useRef(null);
  const {
    importing,
    importResult,
    importProgress,
    importJoinedInFlight,
    handleRefreshPlaylists,
  } = useQobuzPlaylistRefresh();

  const [importingFavs, setImportingFavs] = useState(false);
  const [favImportResult, setFavImportResult] = useState(null);
  const [favImportProgress, setFavImportProgress] = useState(null);
  const handleRefreshFavorites = useCallback(async () => {
    if (importingFavs) return;
    setImportingFavs(true);
    setFavImportResult(null);
    setFavImportProgress(null);
    try {
      const { runQobuzFavoritesImport } = await import('../services/qobuzFavoritesImport');
      const result = await runQobuzFavoritesImport({
        onProgress: (p) => setFavImportProgress(p),
      });
      setFavImportResult(result);
    } catch (err) {
      setFavImportResult({ error: err?.message || 'Import failed', done: true, imported: 0, processed: 0, totalFound: 0 });
    } finally {
      setImportingFavs(false);
      setFavImportProgress(null);
    }
  }, [importingFavs]);

  useEffect(() => {
    if (qobuzConnecting) {
      setConnectingElapsed(0);
      connectingTimerRef.current = setInterval(() => {
        setConnectingElapsed((p) => p + 1);
      }, 1000);
    } else {
      if (connectingTimerRef.current) {
        clearInterval(connectingTimerRef.current);
        connectingTimerRef.current = null;
      }
      setConnectingElapsed(0);
    }
    return () => { if (connectingTimerRef.current) clearInterval(connectingTimerRef.current); };
  }, [qobuzConnecting]);

  const handleLegacyLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const ok = await qobuzLogin(email, password);
      if (ok) {
        setEmail('');
        setPassword('');
        setShowLegacyLogin(false);
      }
    } catch {
      setLoginError('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleDisconnect = () => {
    qobuzDisconnect();
    setShowDisconnectConfirm(false);
  };

  const handleCancel = () => {
    cancelOAuthLogin();
    setShowLegacyLogin(false);
  };

  const getConnectingStatusText = () => {
    if (connectingElapsed < 3) return 'Opening browser\u2026';
    return 'Complete sign-in at qobuz.com, then return to Maestro';
  };

  const isTimedOut = qobuzError && !qobuzConnecting;
  const isSessionExpired = qobuzError && qobuzError.toLowerCase().includes('session expired');

  return (
    <div style={{
      borderRadius: 14,
      background: 'var(--bg-card)',
      border: '1px solid var(--border-light)',
      overflow: 'hidden',
      padding: 16,
    }}>
      {/* Header — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: (qobuzConnected || isQobuzActive === false) ? 0 : 16, opacity: isQobuzActive === false ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        <span style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <QobuzIcon />
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: isQobuzActive === false ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>Qobuz</span>
        {qobuzConnected && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-success)' }}>Connected</span>
          </span>
        )}
      </div>

      {/* ── Disabled state (highest priority) ── */}
      {isQobuzActive === false && !qobuzConnecting && (
        <div style={{ padding: '10px 0 0' }}>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.55 }}>
            Qobuz is currently disabled in Maestro.
          </p>
          <button
            onClick={() => {
              const el = document.querySelector('[data-local-only-section]');
              if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
            }}
            style={{
              background: 'none', border: 'none', padding: 0, marginTop: 6,
              fontSize: 12, color: 'var(--text-tertiary)', cursor: 'pointer',
              textDecoration: 'underline', textUnderlineOffset: 2, opacity: 0.7,
            }}
          >Enable Qobuz in &lsquo;Local files only&rsquo; settings below</button>
        </div>
      )}

      {/* ── State 2: Connecting ── */}
      {qobuzConnecting && (
        <div style={{ animation: 'fade-in-up 0.2s ease-out' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 0',
          }}>
            <span style={{
              width: 18, height: 18, border: '2px solid var(--accent-primary)',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', flexShrink: 0,
            }} />
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {getConnectingStatusText()}
            </span>
          </div>
          <button
            onClick={handleCancel}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: 13, fontWeight: 500, color: 'var(--text-tertiary)',
              cursor: 'pointer', textDecoration: 'underline',
              textUnderlineOffset: 2,
            }}
          >Cancel</button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── State 2b: Error / Timeout ── */}
      {isQobuzActive !== false && !qobuzConnecting && qobuzError && (
        <div style={{ animation: 'fade-in-up 0.2s ease-out' }}>
          <div style={{
            padding: '12px 14px', borderRadius: 10, marginBottom: 12,
            background: 'color-mix(in srgb, var(--color-error) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-error) 20%, transparent)',
          }}>
            <p style={{ fontSize: 13, color: 'var(--color-error)', margin: 0, lineHeight: 1.5 }}>
              {isSessionExpired
                ? 'Your Maestro session expired. Please sign back into Maestro, then reconnect Qobuz.'
                : "We didn't hear back from Qobuz. Please try again."}
            </p>
          </div>
          <button
            onClick={() => qobuzLoginOAuth()}
            style={{
              width: '100%', padding: '13px 0', borderRadius: 10, fontSize: 14, fontWeight: 700,
              background: 'var(--accent-primary)', color: '#1a1a2e',
              cursor: 'pointer', border: 'none',
            }}
          >Try again</button>
        </div>
      )}

      {/* ── State 1: Not connected ── */}
      {isQobuzActive !== false && shouldShowQobuzConnectPrompt({ connected: qobuzConnected, connecting: qobuzConnecting, error: qobuzError, linkResolved: qobuzLinkResolved }) && (
        <div style={{ animation: 'fade-in-up 0.2s ease-out' }}>
          <button
            onClick={() => qobuzLoginOAuth()}
            style={{
              width: '100%', height: 48, borderRadius: 10, fontSize: 14, fontWeight: 700,
              background: 'var(--accent-primary)', color: '#1a1a2e',
              cursor: 'pointer', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'opacity 0.15s',
            }}
          >
            Connect your Qobuz account
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          <p style={{
            fontSize: 12, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.55,
            textAlign: 'center',
          }}>
            Maestro uses Qobuz for high-resolution streaming. You'll sign in at qobuz.com in your browser — Maestro never sees your password.
          </p>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              onClick={() => setShowLegacyLogin(!showLegacyLogin)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: 12, color: 'var(--text-tertiary)',
                cursor: 'pointer', textDecoration: 'underline',
                textUnderlineOffset: 2, opacity: 0.7,
              }}
            >Use email and password instead</button>
          </div>

          {showLegacyLogin && (
            <form onSubmit={handleLegacyLogin} autoComplete="on" style={{
              display: 'flex', flexDirection: 'column', gap: 8,
              marginTop: 14, paddingTop: 14,
              borderTop: '1px solid var(--border-light)',
              animation: 'fade-in-up 0.2s ease-out',
            }}>
              <input
                type="email" placeholder="Qobuz email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-light)', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <input
                type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-light)', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                type="submit" disabled={loginLoading || !email || !password}
                style={{
                  width: '100%', padding: '9px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                  cursor: loginLoading ? 'wait' : 'pointer',
                  opacity: (!email || !password) ? 0.5 : 1,
                  border: '1px solid var(--border-light)',
                }}
              >{loginLoading ? 'Connecting\u2026' : 'Sign In'}</button>
              {(loginError || qobuzError) && (
                <p style={{ fontSize: 12, color: 'var(--color-error)', margin: 0 }}>{loginError || qobuzError}</p>
              )}
            </form>
          )}
        </div>
      )}

      {/* ── State 3: Connected ── */}
      {isQobuzActive !== false && qobuzConnected && !qobuzConnecting && (
        <div style={{ paddingTop: 14, animation: 'fade-in-up 0.2s ease-out' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {qobuzUser?.name && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Name</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{qobuzUser.name}</span>
              </div>
            )}
            {qobuzUser?.email && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Email</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{qobuzUser.email}</span>
              </div>
            )}
            {qobuzUser?.subscription && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Plan</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{qobuzUser.subscription}</span>
              </div>
            )}
          </div>

          <QobuzPlaylistRefreshPanel
            t={t}
            importing={importing}
            importJoinedInFlight={importJoinedInFlight}
            importProgress={importProgress}
            importResult={importResult}
            onRefresh={handleRefreshPlaylists}
          />

          {/* Favorites refresh */}
          <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Qobuz Favorites</span>
              <button
                onClick={handleRefreshFavorites}
                disabled={importingFavs}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6,
                  background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                  cursor: importingFavs ? 'wait' : 'pointer', opacity: importingFavs ? 0.6 : 1,
                  border: 'none',
                }}
              >{importingFavs ? 'Syncing\u2026' : 'Refresh'}</button>
            </div>
            {importingFavs && favImportProgress && favImportProgress.totalFound > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                Importing: {favImportProgress.imported} of {favImportProgress.totalFound} favorites…
              </div>
            )}
            {!importingFavs && favImportResult && favImportResult.done && !favImportResult.error && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.45 }}>
                Imported {favImportResult.imported} favorite{favImportResult.imported === 1 ? '' : 's'} from Qobuz.
              </div>
            )}
            {!importingFavs && favImportResult?.error && (
              <div style={{ fontSize: 11, color: 'var(--color-error)' }}>{favImportResult.error}</div>
            )}
          </div>

          {/* Disconnect link */}
          {!showDisconnectConfirm ? (
            <button
              onClick={() => setShowDisconnectConfirm(true)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: 12, color: 'var(--text-tertiary)',
                cursor: 'pointer', textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >Disconnect</button>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 8,
              background: 'color-mix(in srgb, var(--color-error) 6%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-error) 15%, transparent)',
              animation: 'fade-in-up 0.15s ease-out',
            }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', flex: 1 }}>Disconnect Qobuz?</span>
              <button
                onClick={handleDisconnect}
                style={{
                  fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 6,
                  background: 'var(--color-error)', color: '#fff',
                  cursor: 'pointer', border: 'none',
                }}
              >Yes</button>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 6,
                  background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                  cursor: 'pointer', border: 'none',
                }}
              >No</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LocalOnlySection({ services, toggleServiceActive }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 16 }} data-local-only-section>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Using only local files?</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"
          style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
      {expanded && (
        <div style={{
          marginTop: 10, padding: 14, borderRadius: 12,
          background: 'var(--bg-card)', border: '1px solid var(--border-light)',
          animation: 'fade-in-up 0.2s ease-out',
        }}>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '0 0 12px', lineHeight: 1.5 }}>
            If you only play local files, you can turn off streaming services to simplify Maestro's interface.
          </p>
          {SERVICES_LIST.map((svc) => {
            const isActive = services[svc.id]?.active === true;
            return (
              <div key={svc.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid var(--border-light)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{svc.icon}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{svc.label}</span>
                </div>
                <PillToggle checked={isActive} onChange={() => toggleServiceActive(svc.id)} disabled={svc.comingSoon} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function useAppVersion() {
  const [version, setVersion] = useState(null);
  const [buildDate, setBuildDate] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (typeof window !== 'undefined' && '__TAURI__' in window) {
          const { getConnectorInfo } = await import('../lib/connector');
          const info = await getConnectorInfo();
          if (!cancelled && info?.version) {
            setVersion(info.version);
            if (info.build_timestamp) {
              const ts = parseInt(info.build_timestamp, 10);
              if (ts > 0) {
                const d = new Date(ts * 1000);
                setBuildDate(d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
              }
            }
            return;
          }
        }
      } catch (err) {
        console.error('[useAppVersion] get_connector_info failed:', err);
      }
      if (!cancelled) {
        const ver = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
        setVersion(ver || 'dev');
        const bd = typeof __APP_BUILD_DATE__ !== 'undefined' ? __APP_BUILD_DATE__ : null;
        if (bd) setBuildDate(bd);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { version, buildDate };
}

export default function SettingsSlideout({ open, onClose, locationGuard }) {
  const [remoteInstallState, setRemoteInstallState] = React.useState('idle');
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const { services, setServices, primaryService, setPrimaryService, voiceSearchEnabled, setVoiceSearchEnabled } = useServices();
  const { subscriptionData, fetchSubscriptionStatus, openStripeCheckout } = useSubscription();
  const auth = useAuth();
  const nav = useAppNavigation();
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [showBillingCycleSwitcher, setShowBillingCycleSwitcher] = useState(false);
  const [showFullFeaturedModal, setShowFullFeaturedModal] = useState(false);
  const desktopMode = useDesktopMode();
  const setDesktopMode = useMaestroStore((s) => s.setDesktopMode);
  const isHomeLibraryHost = useMaestroStore((s) => s.isHomeLibraryHost);
  const qobuzUser = useQobuzStore((s) => s.user);
  const qobuzConnected = useQobuzStore((s) => s.connected);
  const qobuzDisconnect = useQobuzStore((s) => s.disconnect);
  const qobuzLogin = useQobuzStore((s) => s.login);
  const qobuzLoginOAuth = useQobuzStore((s) => s.loginOAuth);
  const qobuzConnecting = useQobuzStore((s) => s.connecting);
  const qobuzError = useQobuzStore((s) => s.error);
  const { theme, setTheme, displayScale, setDisplayScale } = useTheme();
  const { t, lang, setLanguage } = useTranslation();
  const continuousPlay = useMaestroStore((s) => s.continuousPlay);
  const isFullMode = desktopMode === 'full';
  const { version: appVersion, buildDate: appBuildDate } = useAppVersion();




  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch(`${CLOUD_API_URL}/api/maestro/billing/currency`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPricing(d); })
      .catch(() => {});
  }, [open]);

  const [updateStatus, setUpdateStatus] = useState(null); // null | 'checking' | 'up-to-date' | 'update-available' | 'check-failed' | 'error'
  const [latestVersion, setLatestVersion] = useState(null);
  const [updateDownloadUrl, setUpdateDownloadUrl] = useState(null);
  const [updateDetailMessage, setUpdateDetailMessage] = useState(null);
  const [exportLogsBusy, setExportLogsBusy] = useState(false);
  const [exportLogsError, setExportLogsError] = useState(null);
  const isTauriApp = typeof window !== 'undefined' && ('__TAURI__' in window || '__MAESTRO_CONNECTOR__' in window);
  const showDeveloperSettings = isTauriApp && canShowDeveloperSettings(auth);

  const [showUninstallConfirm, setShowUninstallConfirm] = useState(false);
  const [uninstallDone, setUninstallDone] = useState(false);
  const [uninstallLoading, setUninstallLoading] = useState(false);

  const handleFullUninstall = useCallback(async () => {
    setUninstallLoading(true);
    try {
      const connector = await import('../lib/connector');
      await connector.fullUninstall();
      setUninstallDone(true);
      setTimeout(async () => {
        try {
          const core = window.__TAURI__?.core || window.__TAURI__;
          if (core?.invoke) {
            await core.invoke('plugin:shell|open', { path: '/Applications' });
          }
        } catch {}
        setTimeout(() => {
          try { window.__TAURI__?.process?.exit?.(0); } catch {}
          try { window.close(); } catch {}
        }, 2000);
      }, 3000);
    } catch (err) {
      console.error('[Uninstall] failed:', err);
      setUninstallLoading(false);
    }
  }, []);

  const [localCoreEnabled, setLocalCoreEnabled] = useState(() => {
    try {
      return localStorage.getItem('maestro_local_core_enabled') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!open || !showDeveloperSettings) return;
    let cancelled = false;
    (async () => {
      try {
        const { getLocalCoreEnabled } = await import('../lib/connector');
        const enabled = await getLocalCoreEnabled();
        if (!cancelled) setLocalCoreEnabled(enabled);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [open, showDeveloperSettings]);

  const handleLocalCoreToggle = useCallback(
    async (enabled) => {
      setLocalCoreEnabled(enabled);
      try {
        localStorage.setItem('maestro_local_core_enabled', enabled ? 'true' : 'false');
      } catch {}
      if (isTauriApp) {
        try {
          const { setLocalCoreEnabled: setConnectorLocalCore } = await import('../lib/connector');
          await setConnectorLocalCore(enabled);
        } catch (e) {
          console.error('[Settings] set local-core toggle:', e);
        }
      }
    },
    [isTauriApp],
  );

  const [ellaVoiceMode, setEllaVoiceModeState] = useState(() => {
    try {
      const raw = localStorage.getItem('maestro_valet_prefs');
      if (raw) { const p = JSON.parse(raw); if (p.ellaVoiceMode) return p.ellaVoiceMode; }
    } catch {}
    return 'duck';
  });
  const handleEllaVoiceMode = useCallback((mode) => {
    setEllaVoiceModeState(mode);
    try {
      const raw = localStorage.getItem('maestro_valet_prefs');
      const prefs = raw ? JSON.parse(raw) : {};
      prefs.ellaVoiceMode = mode;
      localStorage.setItem('maestro_valet_prefs', JSON.stringify(prefs));
    } catch {}
  }, []);




  useEffect(() => {
    if (!open) {
      setUpdateStatus(null);
      setUpdateDetailMessage(null);
      setExportLogsError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!isTauriApp) return undefined;
    const tauri = window.__TAURI__;
    if (!tauri?.event?.listen) return undefined;

    const unlisteners = [];
    let cancelled = false;

    (async () => {
      const listen = tauri.event.listen.bind(tauri.event);
      if (cancelled) return;

      const loadUpdaterUi = () => import('../utils/updaterUi');

      unlisteners.push(await listen('maestro://update-checking', () => {
        setUpdateDetailMessage(null);
        setUpdateStatus('checking');
      }));
      unlisteners.push(await listen('maestro://update-downloading', (e) => {
        setUpdateDetailMessage(null);
        const version = typeof e?.payload === 'string' ? e.payload : '';
        if (version) setLatestVersion(version.replace(/^v/i, ''));
        setUpdateStatus('checking');
      }));
      unlisteners.push(await listen('maestro://update-up-to-date', () => {
        setUpdateDetailMessage(null);
        setUpdateDownloadUrl(null);
        setUpdateStatus('up-to-date');
      }));
      unlisteners.push(await listen('maestro://update-none', () => {
        setUpdateDetailMessage(null);
        setUpdateDownloadUrl(null);
        setUpdateStatus('up-to-date');
      }));
      unlisteners.push(await listen('maestro://update-available', async (e) => {
        const { parseUpdateAvailablePayload } = await loadUpdaterUi();
        const { version, downloadUrl } = parseUpdateAvailablePayload(e?.payload);
        setUpdateDetailMessage(null);
        if (version) setLatestVersion(version);
        setUpdateDownloadUrl(downloadUrl || null);
        setUpdateStatus('update-available');
      }));

      const onCheckFailed = async (e) => {
        const { parseUpdateCheckFailedPayload, logUpdaterDiagnostic } = await loadUpdaterUi();
        const message = parseUpdateCheckFailedPayload(e?.payload);
        logUpdaterDiagnostic('check-failed', message, { source: 'SettingsSlideout' });
        setUpdateDetailMessage(message);
        setUpdateStatus('check-failed');
      };
      unlisteners.push(await listen('maestro://update-check-failed', onCheckFailed));
      unlisteners.push(await listen('maestro://update-check-error', onCheckFailed));

      unlisteners.push(await listen('maestro://update-error', async (e) => {
        const { parseUpdateErrorPayload, logUpdaterDiagnostic } = await loadUpdaterUi();
        const parsed = parseUpdateErrorPayload(e?.payload);
        logUpdaterDiagnostic('install-error', parsed.message, {
          source: 'SettingsSlideout',
          remote_version: parsed.remoteVersion || undefined,
        });
        setUpdateDetailMessage(parsed.message);
        if (parsed.remoteVersion) setLatestVersion(parsed.remoteVersion.replace(/^v/i, ''));
        setUpdateStatus('error');
      }));

      unlisteners.push(await listen('maestro://help-error', async (e) => {
        const message = typeof e?.payload === 'string' ? e.payload : String(e?.payload ?? 'Export failed');
        const { showImperativeToast } = await import('../providers/UIProvider');
        showImperativeToast(`Could not export logs: ${message}`, 6000);
        setExportLogsBusy(false);
      }));
    })();

    return () => {
      cancelled = true;
      unlisteners.forEach((fn) => { try { fn(); } catch {} });
    };
  }, [isTauriApp]);

  const handleExportLogs = useCallback(async () => {
    if (!isTauriApp || exportLogsBusy) return;
    await runPrimaryAction({
      actionLabel: 'export logs',
      onError: setExportLogsError,
      onRunning: setExportLogsBusy,
      run: async () => {
        const { exportLogsZip, isConnectorAvailable } = await import('../lib/connector');
        if (!isConnectorAvailable()) {
          throw new Error('Export Logs is only available in the Maestro desktop app');
        }
        const path = await exportLogsZip();
        if (!path) {
          throw new Error('Export finished but no file path was returned');
        }
        const leaf = path.split(/[/\\]/).pop() || path;
        const { showImperativeToast } = await import('../providers/UIProvider');
        showImperativeToast(`Logs exported to Desktop: ${leaf}`, 6000);
      },
    });
  }, [exportLogsBusy, isTauriApp]);

  const handleCheckForUpdates = useCallback(async () => {
    setUpdateStatus('checking');
    setUpdateDetailMessage(null);
    setUpdateDownloadUrl(null);
    try {
      const isTauri = typeof window !== 'undefined' && ('__TAURI__' in window || '__MAESTRO_CONNECTOR__' in window);
      if (isTauri) {
        const [
          { triggerConnectorUpdateCheck },
          {
            parseLegacyManualCheckResult,
            logUpdaterDiagnostic,
            runLegacyConnectorUpdateCheckFallback,
          },
          { connectorSupportsEventBasedUpdateCheck, getConnectorVersion },
        ] = await Promise.all([
          import('../lib/connector'),
          import('../utils/updaterUi'),
          import('../utils/connectorVersion'),
        ]);
        const installedVersion = getConnectorVersion() || appVersion;
        if (!connectorSupportsEventBasedUpdateCheck()) {
          const legacy = await runLegacyConnectorUpdateCheckFallback(installedVersion);
          setUpdateDetailMessage(legacy.message);
          if (legacy.version) setLatestVersion(legacy.version);
          if (legacy.downloadUrl) setUpdateDownloadUrl(legacy.downloadUrl);
          setUpdateStatus(legacy.status);
          if (legacy.status === 'check-failed') {
            logUpdaterDiagnostic('check-failed', legacy.message, { source: 'SettingsSlideout', legacy: true });
          }
          return;
        }
        const result = await triggerConnectorUpdateCheck();
        if (typeof result === 'string' && result.trim()) {
          const legacy = parseLegacyManualCheckResult(result);
          setUpdateDetailMessage(legacy.message);
          if (legacy.version) setLatestVersion(legacy.version);
          setUpdateStatus(legacy.status);
          if (legacy.status === 'check-failed') {
            logUpdaterDiagnostic('check-failed', legacy.message, { source: 'SettingsSlideout', legacy: true });
          }
        }
        return;
      }

      const res = await fetch(`${window.location.origin}/version.json?_=${Date.now()}`);
      if (!res.ok) throw new Error('Could not check for updates');
      const data = await res.json();
      const serverVersion = data.version;
      const webVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;
      if (serverVersion && webVersion && serverVersion !== webVersion) {
        setLatestVersion(serverVersion);
        setUpdateStatus('update-available');
      } else {
        setUpdateStatus('up-to-date');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not check for updates';
      try {
        const { logUpdaterDiagnostic } = await import('../utils/updaterUi');
        logUpdaterDiagnostic('check-failed', message, { source: 'SettingsSlideout' });
      } catch {}
      setUpdateDetailMessage(message);
      setUpdateStatus('check-failed');
    }
  }, [appVersion]);

  const handleUpdateButtonClick = useCallback(async () => {
    if (updateStatus === 'update-available') {
      const [{ openManualDownloadUrl, resolveManualDownloadUrl }, { resolveOfferedDownloadUrl }] = await Promise.all([import('../utils/updaterUi'), import('../utils/connectorOfferedDownload')]);
      await openManualDownloadUrl(isTauriApp ? await resolveOfferedDownloadUrl(latestVersion, updateDownloadUrl) : (updateDownloadUrl || await resolveManualDownloadUrl(latestVersion)));
      return;
    }
    await handleCheckForUpdates();
  }, [updateStatus, updateDownloadUrl, latestVersion, handleCheckForUpdates, isTauriApp]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      setSubLoading(true);
      fetchSubscriptionStatus().finally(() => setSubLoading(false));
      if (locationGuard?.refreshLocations) locationGuard.refreshLocations();
    } else {
      setVisible(false);
      setShowCancelConfirm(false);
      setShowBillingCycleSwitcher(false);
      const timer = setTimeout(() => setShouldRender(false), 350);
      return () => clearTimeout(timer);
    }
  }, [open, fetchSubscriptionStatus]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);




  const toggleServiceActive = useCallback((id) => {
    setServices((prev) => {
      const svc = prev[id];
      if (!svc) return prev;
      const newActive = !svc.active;
      const noAuth = ['local', 'live_radio', 'ella_radio', 'octave_radio'];
      return {
        ...prev,
        [id]: { ...svc, active: newActive, connected: noAuth.includes(id) ? newActive : svc.connected },
      };
    });
  }, [setServices]);

  const handleConnect = useCallback((id) => {
    if (id === 'qobuz') {
      onClose();
      nav.goQobuz();
    }
  }, [onClose, nav]);

  const [cancelDone, setCancelDone] = useState(false);
  const [cancelMethod, setCancelMethod] = useState(null);
  const handleCancelSubscription = useCallback(async () => {
    const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
    if (!token) return;
    setPortalLoading(true);
    try {
      const res = await fetch(`${CLOUD_API_URL}/api/stripe/cancel-at-period-end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setCancelMethod(data?.method ?? null);
        setCancelDone(true);
        if (fetchSubscriptionStatus) fetchSubscriptionStatus();
      } else {
        console.error('[Settings] cancel error:', data);
      }
    } catch (e) {
      console.error('[Settings] cancel error', e);
    } finally {
      setPortalLoading(false);
    }
  }, [fetchSubscriptionStatus]);

  const handleOpenBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      if (shouldRouteManageToCheckout(subscriptionData)
        || subscriptionData?.subscribe_via_checkout
        || subscriptionData?.resubscribe_via_checkout) {
        await openStripeCheckout('monthly');
        setPortalLoading(false);
        return;
      }
      const result = await billingService.getStripeSessionUrl();
      if (result.status === 'portal') {
        window.location.href = result.url;
        return;
      }
      if (result.status === 'checkout') {
        await openStripeCheckout('monthly');
        setPortalLoading(false);
        return;
      }
      const { showImperativeToast } = await import('../providers/UIProvider');
      showImperativeToast(
        result.message || 'Your subscription is not active yet. Subscribe to add a payment method.',
        6000,
      );
    } catch (e) {
      console.error('[Settings] billing portal error:', e);
      const { showImperativeToast } = await import('../providers/UIProvider');
      showImperativeToast('Unable to reach the billing server. Please check your connection and try again.', 6000);
    }
    setPortalLoading(false);
  }, [subscriptionData, openStripeCheckout]);

  const [planSwitchState, setPlanSwitchState] = useState('idle'); // idle | loading | done | error
  const [planSwitchTarget, setPlanSwitchTarget] = useState(null); // 'annual' | 'monthly'

  const handleSwitchToAnnual = useCallback(async () => {
    const proceed = window.confirm(
      'Switch to annual billing now? This applies your annual plan immediately and may create an immediate charge or proration on Stripe.'
    );
    if (!proceed) return;
    setPlanSwitchState('loading');
    setPlanSwitchTarget('annual');
    try {
      const result = await billingService.switchToAnnual();
      if (result?.status === 'switched' || result?.error === 'Already on annual plan') {
        setPlanSwitchState('done');
        if (fetchSubscriptionStatus) await fetchSubscriptionStatus();
        setTimeout(() => setPlanSwitchState('idle'), 2500);
      } else {
        setPlanSwitchState('error');
        setTimeout(() => setPlanSwitchState('idle'), 4000);
      }
    } catch {
      setPlanSwitchState('error');
      setTimeout(() => setPlanSwitchState('idle'), 4000);
    }
  }, [fetchSubscriptionStatus]);

  const handleSwitchToMonthly = useCallback(async () => {
    const proceed = window.confirm(
      'Switch to monthly billing now? This updates your Stripe billing cycle immediately and may create a proration adjustment.'
    );
    if (!proceed) return;
    setPlanSwitchState('loading');
    setPlanSwitchTarget('monthly');
    try {
      const result = await billingService.switchToMonthly();
      if (result?.status === 'switched' || result?.error === 'Already on monthly plan') {
        setPlanSwitchState('done');
        if (fetchSubscriptionStatus) await fetchSubscriptionStatus();
        setTimeout(() => setPlanSwitchState('idle'), 2500);
      } else {
        setPlanSwitchState('error');
        setTimeout(() => setPlanSwitchState('idle'), 4000);
      }
    } catch {
      setPlanSwitchState('error');
      setTimeout(() => setPlanSwitchState('idle'), 4000);
    }
  }, [fetchSubscriptionStatus]);

  const [resubError, setResubError] = useState(null);
  const handleResubscribe = useCallback(async () => {
    setResubError(null);
    const currentTier = subscriptionData?.subscription_tier;
    if (currentTier === 'cancelling') {
      const token = (() => { try { const s = localStorage.getItem('maestro_auth'); return s ? JSON.parse(s).accessToken : null; } catch { return null; } })();
      if (!token) { setResubError('No auth token found'); return; }
      setPortalLoading(true);
      try {
        const res = await fetch(`${CLOUD_API_URL}/api/stripe/reactivate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setCancelDone(false);
          setShowCancelConfirm(false);
          setResubError(null);
          fetchSubscriptionStatus();
        } else {
          console.error('[Settings] reactivate error:', res.status, data);
          setResubError(data.error || `Server error (${res.status})`);
        }
      } catch (e) {
        console.error('[Settings] reactivate error', e);
        setResubError(e.message || 'Network error');
      } finally {
        setPortalLoading(false);
      }
    } else {
      setCheckoutLoading(true);
      const ok = await openStripeCheckout();
      setCheckoutLoading(false);
      if (ok) {
        const fresh = await fetchSubscriptionStatus();
        if (shouldIgnoreAlreadySubscribed(fresh || subscriptionData)) {
          const { showImperativeToast } = await import('../providers/UIProvider');
          showImperativeToast('Complete checkout to activate paid billing.', 6000);
          return;
        }
        if (shouldShowAlreadySubscribed(fresh || subscriptionData)) {
          const { showImperativeToast } = await import('../providers/UIProvider');
          showImperativeToast("You're already subscribed — check Your Plan above.", 5000);
          return;
        }
        onClose();
      }
    }
  }, [subscriptionData, fetchSubscriptionStatus, onClose, openStripeCheckout]);

  const handleSignOut = useCallback(async () => {
    onClose();
    try {
      await authSignOut();
    } catch {
      try { localStorage.removeItem('maestro_auth'); } catch {}
      try { sessionStorage.removeItem('maestro_refresh_failed'); } catch {}
    }
  }, [onClose]);

  if (!shouldRender) return null;

  const sub = subscriptionData;
  const tier = sub?.subscription_tier || 'none';
  const badge = tierLabel(tier, sub?.trial_days_remaining);
  const cancelEndDate = getCancelAccessEndDateIso(sub, tier);
  const displayedBillingInterval = resolveDisplayedBillingInterval(
    sub,
    planSwitchTarget,
    planSwitchState,
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Panel */}
      <div
        data-settings-slideout
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 480,
          background: 'var(--bg-primary)',
          borderLeft: '1px solid var(--border-light)',
          borderRadius: '20px 0 0 20px',
          boxShadow: 'var(--shadow-elevated)',
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px', paddingTop: 'max(18px, env(safe-area-inset-top, 18px))', display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid var(--border-light)', flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'var(--bg-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-secondary)', cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            title="Close settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>{t('nav.settings')}</h1>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 32px' }} className="no-scrollbar">

          {/* ── QUICK SETTINGS (top of panel) ── */}

          {/* Theme picker — Light / Dark / High Contrast */}
          <div style={{ padding: '16px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('settings.theme')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                {t('settings.theme_desc')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              {[
                { value: 'light', label: t('settings.theme_light') },
                { value: 'dark', label: t('settings.theme_dark') },
                { value: 'high-contrast', label: t('settings.theme_high_contrast') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  style={{
                    flex: 1, padding: '7px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: 'none', outline: 'none',
                    background: theme === opt.value ? '#d4a843' : 'var(--bg-secondary)',
                    color: theme === opt.value ? '#000' : 'var(--text-secondary)',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Display Size */}
          <div style={{ padding: '14px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('settings.display_size')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                {t('settings.display_size_desc')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-light)' }}>
              {[
                { value: 0.85, label: t('settings.size_small') },
                { value: 1.0, label: t('settings.size_default') },
                { value: 1.15, label: t('settings.size_large') },
                { value: 1.3, label: t('settings.size_xl') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDisplayScale(opt.value)}
                  style={{
                    flex: 1, padding: '7px 6px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: 'none', outline: 'none',
                    background: displayScale === opt.value ? '#d4a843' : 'var(--bg-secondary)',
                    color: displayScale === opt.value ? '#000' : 'var(--text-secondary)',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Language picker */}
          <div data-guide-id="language-picker" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('settings.language')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                {t('settings.language_desc')}
              </div>
            </div>
            <select
              value={lang}
              onChange={(e) => setLanguage(e.target.value)}
              style={{
                padding: '6px 28px 6px 10px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                border: '1px solid var(--border-light)', cursor: 'pointer',
                appearance: 'none', WebkitAppearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
                minWidth: 120,
              }}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('settings.voice_search')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                {t('settings.voice_search_desc')}
              </div>
            </div>
            <PillToggle checked={voiceSearchEnabled} onChange={setVoiceSearchEnabled} />
          </div>

          {/* Ella voice mode */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{t('settings.ella_voice')}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                {t('settings.ella_voice_desc')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-light)', flexShrink: 0 }}>
              {[
                { value: 'duck', label: t('settings.ella_voice_duck') },
                { value: 'pause', label: t('settings.ella_voice_pause') },
                { value: 'off', label: t('settings.ella_voice_off') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleEllaVoiceMode(opt.value)}
                  style={{
                    padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: 'none', outline: 'none',
                    background: ellaVoiceMode === opt.value ? '#d4a843' : 'var(--bg-secondary)',
                    color: ellaVoiceMode === opt.value ? '#000' : 'var(--text-secondary)',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <EllaRadioBansSettings />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Continuous Play</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                When on, tapping a track plays the rest of the album. When off, plays only that track.
              </div>
            </div>
            <PillToggle
              checked={continuousPlay}
              onChange={(val) => useMaestroStore.getState().setContinuousPlay(val)}
            />
          </div>

          {/* ── STREAMING SERVICES ── */}
          <SectionHeader>Streaming Services</SectionHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Qobuz — redesigned panel */}
            <QobuzPanel
              qobuzConnected={qobuzConnected}
              qobuzUser={qobuzUser}
              qobuzDisconnect={qobuzDisconnect}
              qobuzLogin={qobuzLogin}
              qobuzLoginOAuth={qobuzLoginOAuth}
              qobuzConnecting={qobuzConnecting}
              qobuzError={qobuzError}
              isQobuzActive={services.qobuz?.active}
            />

            {/* Other services */}
            {SERVICES_LIST.filter(svc => svc.id !== 'qobuz').map((svc) => {
              const s = services[svc.id] || {};
              const isConnected = s.connected === true;
              const isActive = s.active === true;
              const isPrimary = primaryService === svc.id;
              return (
                <div
                  key={svc.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 14,
                    background: isPrimary ? 'rgba(201,168,76,0.06)' : 'var(--bg-card)',
                    border: isPrimary ? '1px solid rgba(201,168,76,0.15)' : '1px solid var(--border-light)',
                    transition: 'background 0.15s',
                  }}
                >
                  <span style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {svc.icon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{svc.label}</span>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: isConnected ? '#22c55e' : 'var(--text-tertiary)',
                        opacity: isConnected ? 1 : 0.4,
                        flexShrink: 0,
                      }} />
                      {isPrimary && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                          padding: '2px 6px', borderRadius: 4,
                          background: 'rgba(201,168,76,0.15)', color: GOLD,
                        }}>Primary</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {isConnected ? 'Connected' : svc.comingSoon ? 'Coming soon' : svc.noAuth ? (isActive ? 'Enabled' : 'Disabled') : svc.connectable ? 'Not connected' : 'Disabled'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isConnected && !isPrimary && !svc.noAuth && (
                      <button
                        onClick={() => setPrimaryService(svc.id)}
                        style={{
                          fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8,
                          background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                          cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                        }}
                      >Set Primary</button>
                    )}
                    {!isConnected && svc.connectable && (
                      <button
                        onClick={() => handleConnect(svc.id)}
                        style={{
                          fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8,
                          background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                          cursor: 'pointer', transition: 'background 0.15s',
                        }}
                      >Connect</button>
                    )}
                    <PillToggle checked={isActive} onChange={() => toggleServiceActive(svc.id)} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Local-only mode (State 4) ── */}
          <LocalOnlySection services={services} toggleServiceActive={toggleServiceActive} />

          {/* ── SUBSCRIPTION ── */}
          <SectionHeader>Subscription</SectionHeader>
          <div style={{
            padding: 16, borderRadius: 14,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
          }}>
            {subLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ width: 100, height: 16, borderRadius: 6, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ width: 60, height: 20, borderRadius: 10, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ width: 90, height: 13, borderRadius: 4, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                    <div style={{ width: 110, height: 13, borderRadius: 4, background: 'var(--bg-secondary)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  </div>
                ))}
                <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
              </div>
            ) : (
              <>
                {/* Status header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Your Plan</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                    background: `${badge.color}18`, color: badge.color,
                  }}>
                    {badge.text}
                  </span>
                </div>

                {/* Detail rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>

                  {/* BETA ACCESS */}
                  {tier === 'beta' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      <DetailRow label="Access" value="Full access" color="#8b5cf6" />
                      {sub?.streaming_mode && (
                        <DetailRow label="Streaming" value={sub.streaming_mode === 'full' ? 'Full quality' : '30s previews'} color={sub.streaming_mode === 'full' ? '#22c55e' : undefined} />
                      )}
                    </>
                  )}

                  {/* ACTIVE subscription */}
                  {tier === 'active' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      {formatBillingPlanLabel(sub?.billing_interval) && (
                        <DetailRow label="Plan" value={formatBillingPlanLabel(sub?.billing_interval)} />
                      )}
                      {!isIosShell() && formatSubscriptionPriceLine(sub) && (
                        <DetailRow label="Price" value={formatSubscriptionPriceLine(sub)} />
                      )}
                      {getActiveRenewalDateIso(sub) && (
                        <DetailRow label="Renews on" value={formatDate(getActiveRenewalDateIso(sub))} />
                      )}
                      {sub?.streaming_mode && (
                        <DetailRow label="Streaming" value={sub.streaming_mode === 'full' ? 'Full quality' : '30s previews'} color={sub.streaming_mode === 'full' ? '#22c55e' : undefined} />
                      )}
                    </>
                  )}

                  {/* TRIAL */}
                  {tier === 'trial' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      {sub?.trial_conversion === 'will_convert' && (sub?.will_convert_at || sub?.trial_ends_at) && (
                        <DetailRow label="Billing begins" value={formatDate(sub.will_convert_at || sub.trial_ends_at)} color={GOLD} />
                      )}
                      {sub?.trial_conversion !== 'will_convert' && sub?.trial_ends_at && (
                        <DetailRow label="Trial ends" value={formatDate(sub.trial_ends_at)} color={GOLD} />
                      )}
                      {sub?.trial_days_remaining != null && (
                        <DetailRow
                          label="Days remaining"
                          value={`${sub.trial_days_remaining} day${sub.trial_days_remaining !== 1 ? 's' : ''}`}
                          color={sub.trial_days_remaining <= 3 && sub?.trial_conversion !== 'will_convert' ? '#ef4444' : GOLD}
                        />
                      )}
                      {sub?.streaming_mode && (
                        <DetailRow label="Streaming" value={sub.streaming_mode === 'full' ? 'Full quality' : '30s previews'} color={sub.streaming_mode === 'full' ? '#22c55e' : undefined} />
                      )}
                    </>
                  )}

                  {/* CANCELLING — cancelled but still has access */}
                  {tier === 'cancelling' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      {buildCancellingEntitlementRows(sub).map((row) => (
                        <DetailRow key={row.label} label={row.label} value={row.value} />
                      ))}
                      {sub?.subscription_ends_at && <DetailRow label="Access until" value={formatDate(sub.subscription_ends_at)} color="#f59e0b" />}
                      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '6px 0 0' }}>
                        Your subscription has been cancelled. You still have full access until the date above. Changed your mind? You can resubscribe anytime.
                      </p>
                    </>
                  )}

                  {/* GRACE PERIOD — payment failed */}
                  {tier === 'grace' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      <DetailRow label="Payment" value="Your last payment didn't go through" color="#f59e0b" />
                      {sub?.subscription_ends_at && <DetailRow label="Access until" value={formatDate(sub.subscription_ends_at)} color="#f59e0b" />}
                      <p style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.5, margin: '6px 0 0' }}>
                        We'll keep trying your card. You can also update your payment method to keep your access.
                      </p>
                    </>
                  )}

                  {/* EXPIRED */}
                  {tier === 'expired' && (
                    <>
                      {sub?.member_since && <DetailRow label="Member since" value={formatDate(sub.member_since)} />}
                      <DetailRow label="Status" value={sub?.had_trial ? 'Your trial has ended' : 'Your subscription has ended'} color="#ef4444" />
                    </>
                  )}

                  {/* INACTIVE / NO SUBSCRIPTION */}
                  {(tier === 'none' || (!tier && !subLoading)) && (
                    <>
                      {sub?.member_since && <DetailRow label="Account created" value={formatDate(sub.member_since)} />}
                      {isIosShell() ? (
                        <IosSubscriptionManageHint style={{ marginTop: 8 }} />
                      ) : (
                        <>
                          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: '4px 0 0' }}>
                            Subscribe to Maestro for a personal music valet that learns your taste, curates recommendations just for you, and helps you discover music you'll love.
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                            {[
                              'Personal music valet that knows your taste',
                              'Curated recommendations just for you',
                              'Cross-device playback & sync',
                              'Deep music discovery & exploration',
                            ].map((item) => (
                              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {isIosShell() ? (
                    <IosSubscriptionManageHint />
                  ) : (
                    <>
                  {/* Change billing cycle — collapsed for active; trial keeps inline switcher */}
                  {tier === 'active' && sub?.billing_interval && !showBillingCycleSwitcher && (
                    <button
                      type="button"
                      onClick={() => setShowBillingCycleSwitcher(true)}
                      style={{
                        width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                        background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                        cursor: 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      Change billing cycle
                    </button>
                  )}

                  {/* Plan switcher — trial subscribers, or active after expanding change control */}
                  {((tier === 'trial' && displayedBillingInterval) || (tier === 'active' && displayedBillingInterval && showBillingCycleSwitcher)) && (
                    <div style={{
                      padding: 14, borderRadius: 12,
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10, letterSpacing: 0.5 }}>
                        BILLING CYCLE
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={displayedBillingInterval !== 'month' ? handleSwitchToMonthly : undefined}
                          disabled={planSwitchState === 'loading' || displayedBillingInterval === 'month'}
                          style={{
                            flex: 1, padding: '12px 10px', borderRadius: 10, cursor: displayedBillingInterval === 'month' ? 'default' : planSwitchState === 'loading' ? 'wait' : 'pointer',
                            background: displayedBillingInterval === 'month' ? `linear-gradient(135deg, ${GOLD}, #e0c068)` : 'rgba(255,255,255,0.04)',
                            border: displayedBillingInterval === 'month' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                            color: displayedBillingInterval === 'month' ? '#1a1a2e' : 'var(--text-secondary)',
                            transition: 'all 0.2s',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700 }}>Monthly</div>
                          {pricing && <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4 }}>{pricing.symbol}{pricing.monthly}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7 }}>/mo</span></div>}
                        </button>
                        <button
                          onClick={displayedBillingInterval !== 'year' ? handleSwitchToAnnual : undefined}
                          disabled={planSwitchState === 'loading' || displayedBillingInterval === 'year'}
                          style={{
                            flex: 1, padding: '12px 10px', borderRadius: 10, cursor: displayedBillingInterval === 'year' ? 'default' : planSwitchState === 'loading' ? 'wait' : 'pointer',
                            background: displayedBillingInterval === 'year' ? `linear-gradient(135deg, ${GOLD}, #e0c068)` : 'rgba(255,255,255,0.04)',
                            border: displayedBillingInterval === 'year' ? 'none' : '1px solid rgba(255,255,255,0.1)',
                            color: displayedBillingInterval === 'year' ? '#1a1a2e' : 'var(--text-secondary)',
                            transition: 'all 0.2s',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700 }}>Annual</div>
                          {pricing && <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4 }}>{pricing.symbol}{pricing.annual}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7 }}>/yr</span></div>}
                          {pricing && <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{pricing.symbol}{pricing.annualMonthlyEquivalent}/mo</div>}
                        </button>
                      </div>
                      {planSwitchState === 'loading' && (
                        <p style={{ fontSize: 12, color: GOLD, textAlign: 'center', marginTop: 10 }}>
                          Switching to {planSwitchTarget} billing…
                        </p>
                      )}
                      {planSwitchState === 'done' && (
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#22c55e', textAlign: 'center', marginTop: 10 }}>
                          Switched to {planSwitchTarget} billing
                        </p>
                      )}
                      {planSwitchState === 'error' && (
                        <p style={{ fontSize: 12, color: '#ef4444', textAlign: 'center', marginTop: 10 }}>
                          Something went wrong. Please try again.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Choose a plan — for trial users without a Stripe subscription yet */}
                  {tier === 'trial' && !sub?.billing_interval && (
                    <div style={{
                      padding: 14, borderRadius: 12,
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10, letterSpacing: 0.5 }}>
                        CHOOSE YOUR PLAN
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          disabled={checkoutLoading}
                          onClick={async () => {
                            setCheckoutLoading(true);
                            const ok = await openStripeCheckout('monthly');
                            setCheckoutLoading(false);
                            if (ok) onClose();
                          }}
                          style={{
                            flex: 1, padding: '12px 10px', borderRadius: 10,
                            cursor: checkoutLoading ? 'wait' : 'pointer',
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: 'var(--text-secondary)',
                            opacity: checkoutLoading ? 0.5 : 1,
                            transition: 'all 0.2s',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{checkoutLoading ? 'Loading…' : 'Monthly'}</div>
                          {pricing && <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4 }}>{pricing.symbol}{pricing.monthly}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7 }}>/mo</span></div>}
                          <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>30-day free trial</div>
                        </button>
                        <button
                          disabled={checkoutLoading}
                          onClick={async () => {
                            setCheckoutLoading(true);
                            const ok = await openStripeCheckout('annual');
                            setCheckoutLoading(false);
                            if (ok) onClose();
                          }}
                          style={{
                            flex: 1, padding: '12px 10px', borderRadius: 10,
                            cursor: checkoutLoading ? 'wait' : 'pointer',
                            background: `linear-gradient(135deg, ${GOLD}, #e0c068)`,
                            border: 'none',
                            color: '#1a1a2e',
                            opacity: checkoutLoading ? 0.5 : 1,
                            transition: 'all 0.2s',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{checkoutLoading ? 'Loading…' : 'Annual'}</div>
                          {pricing && <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4 }}>{pricing.symbol}{pricing.annual}<span style={{ fontSize: 11, fontWeight: 500, opacity: 0.7 }}>/yr</span></div>}
                          {pricing && <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{pricing.symbol}{pricing.annualMonthlyEquivalent}/mo · 30-day free trial</div>}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Cancel flow — active subscribers only (not already cancelling) */}
                  {(tier === 'active' || tier === 'trial' || tier === 'beta') && !showCancelConfirm && !cancelDone && (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      style={{
                        width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                        background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                        cursor: 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      Cancel Subscription
                    </button>
                  )}

                  {/* Cancel confirmation */}
                  {showCancelConfirm && !cancelDone && (
                    <div style={{
                      padding: 14, borderRadius: 12,
                      background: 'rgba(239,68,68,0.06)',
                      border: '1px solid rgba(239,68,68,0.15)',
                    }}>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
                        {cancelEndDate
                          ? `Your current subscription is good through ${formatDate(cancelEndDate)}. After that date, Maestro will no longer be available. Are you sure?`
                          : 'Are you sure you want to cancel? Your subscription will end immediately and you\'ll lose access to Maestro.'}
                      </p>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={handleCancelSubscription}
                          disabled={portalLoading}
                          style={{
                            flex: 1, fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
                            background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                            cursor: portalLoading ? 'wait' : 'pointer', transition: 'background 0.15s',
                          }}
                        >
                          {portalLoading ? 'Cancelling…' : 'Yes, Cancel'}
                        </button>
                        <button
                          onClick={() => setShowCancelConfirm(false)}
                          style={{
                            flex: 1, fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
                            background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                            cursor: 'pointer', transition: 'background 0.15s',
                          }}
                        >
                          No, Keep It
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Cancellation confirmed */}
                  {cancelDone && (() => {
                    const cancelCopy = getCancelConfirmationCopy(cancelMethod);
                    return (
                    <div style={{
                      padding: 14, borderRadius: 12,
                      background: 'rgba(34,197,94,0.06)',
                      border: '1px solid rgba(34,197,94,0.15)',
                      textAlign: 'center',
                    }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>
                        {cancelCopy.title}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {cancelCopy.body}
                      </p>
                    </div>
                    );
                  })()}

                  {/* Update payment for grace period */}
                  {tier === 'grace' && (
                    <button
                      onClick={handleOpenBillingPortal}
                      disabled={portalLoading}
                      style={{
                        width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                        background: '#f59e0b', color: '#1a1a2e', cursor: portalLoading ? 'wait' : 'pointer',
                      }}
                    >
                      {portalLoading ? 'Loading…' : 'Update Payment Method'}
                    </button>
                  )}

                  {/* Undo cancel / resubscribe */}
                  {sub?.can_renew && (
                    <>
                      <button
                        onClick={handleResubscribe}
                        disabled={portalLoading}
                        style={{
                          width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                          background: GOLD, color: '#1a1a2e', cursor: portalLoading ? 'wait' : 'pointer',
                        }}
                      >
                        {portalLoading
                          ? (tier === 'cancelling' ? 'Resuming…' : 'Loading…')
                          : (tier === 'cancelling' ? 'Resume Subscription' : 'Resubscribe')}
                      </button>
                      {tier === 'cancelling' && (
                        <button
                          onClick={handleOpenBillingPortal}
                          disabled={portalLoading}
                          style={{
                            width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                            background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                            cursor: portalLoading ? 'wait' : 'pointer',
                          }}
                        >
                          {portalLoading ? 'Loading…' : 'Open Billing Portal'}
                        </button>
                      )}
                      {resubError && (
                        <p style={{ fontSize: 12, color: '#ef4444', textAlign: 'center', margin: '6px 0 0' }}>
                          {resubError}
                        </p>
                      )}
                    </>
                  )}

                  {/* Subscribe */}
                  {(tier === 'none' || tier === 'expired' || !tier) && !sub?.can_renew && (
                    <button
                      disabled={checkoutLoading}
                      onClick={async () => {
                        setCheckoutLoading(true);
                        const ok = await openStripeCheckout();
                        setCheckoutLoading(false);
                        if (ok) onClose();
                      }}
                      style={{
                        width: '100%', fontSize: 14, fontWeight: 700, padding: '12px 0', borderRadius: 10,
                        background: GOLD, color: '#1a1a2e',
                        cursor: checkoutLoading ? 'wait' : 'pointer',
                        opacity: checkoutLoading ? 0.6 : 1,
                        transition: 'opacity 0.15s',
                      }}
                    >
                      {checkoutLoading ? 'Loading…' : 'Subscribe'}
                    </button>
                  )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── SUPPORT ── */}
          <SectionHeader>Support</SectionHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div
              aria-disabled="true"
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px', borderRadius: 14,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                cursor: 'default',
                opacity: 0.5,
                transition: 'background 0.15s',
              }}
            >
              <span style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(201,168,76,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Ask Maestro</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Coming soon
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </div>

            {isTauriApp && (
              <>
              <PrimaryActionError
                message={exportLogsError}
                onRetry={handleExportLogs}
                onDismiss={() => setExportLogsError(null)}
                testId="export-logs-action-error"
              />
              <button
                type="button"
                onClick={handleExportLogs}
                disabled={exportLogsBusy}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px', borderRadius: 14,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-light)',
                  cursor: exportLogsBusy ? 'wait' : 'pointer',
                  transition: 'background 0.15s',
                  width: '100%',
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'inherit',
                  boxSizing: 'border-box',
                  opacity: exportLogsBusy ? 0.7 : 1,
                }}
              >
                <span style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: 'rgba(201,168,76,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {exportLogsBusy ? 'Exporting Logs…' : 'Export Logs'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    Save diagnostic logs to your Desktop
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
              </>
            )}

            <button
              type="button"
              onClick={() => { onClose(); nav.goSupport(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
                padding: '14px', borderRadius: 14,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                cursor: 'pointer', transition: 'background 0.15s',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                boxSizing: 'border-box',
              }}
            >
              <span style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(201,168,76,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Get Help</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Chat or submit a support ticket
                </div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>

          {/* ── WHAT'S NEW ── */}
          <WhatsNewSection />

          {REMOTE_ACCESS_ENABLED && (
            <>
              <SectionHeader>Home Library Access</SectionHeader>
              <HomeLibraryConnectionSection />
            </>
          )}

          {isFullMode && (
            <>
              <SectionHeader>Home Library</SectionHeader>
              {isHomeLibraryHost ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Local Library</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                        {browseLocalOnHost()}
                      </div>
                    </div>
                    <PillToggle checked={services.local?.active === true} onChange={() => toggleServiceActive('local')} />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      nav.goLocalLibrary('manage');
                    }}
                    style={{
                      width: '100%', marginTop: 8, padding: '10px 14px', borderRadius: 12,
                      border: '1px solid var(--border-light)', background: 'var(--bg-card)',
                      color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Scan music folders now
                  </button>

                  {REMOTE_ACCESS_ENABLED && (
                    <RemoteAccessToggle onInstallStateChange={setRemoteInstallState} />
                  )}

                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const c = await import('../lib/connector');
                        await c.warmLocalNetworkAccess();
                      } catch { /* best-effort */ }
                    }}
                    style={{
                      width: '100%', marginTop: 8, padding: '10px 14px', borderRadius: 12,
                      border: '1px solid var(--border-light)', background: 'var(--bg-card)',
                      color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {allowLocalNetworkAccessLabel()}
                  </button>

                  {REMOTE_ACCESS_ENABLED && (
                    <DaemonStatusSection remoteInstallState={remoteInstallState} />
                  )}
                </>
              ) : (
                <>
                  {REMOTE_ACCESS_ENABLED && <RemoteLibraryViewerNote />}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border-light)', marginTop: REMOTE_ACCESS_ENABLED ? 8 : 0 }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Local Library</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                        Show your home library in the sidebar on this device.
                      </div>
                    </div>
                    <PillToggle checked={services.local?.active === true} onChange={() => toggleServiceActive('local')} />
                  </div>
                </>
              )}
            </>
          )}

          {/* ── ACTIVE LOCATIONS ── */}
          {locationGuard && locationGuard.locations.length > 0 && (
            <>
              <SectionHeader>Active Locations</SectionHeader>
              <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12, lineHeight: 1.5 }}>
                {locationGuard.locations.length} of {locationGuard.limit} locations used.
                Your first location is automatically marked as Home.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {locationGuard.locations.map((loc) => (
                  <LocationCard
                    key={loc.id}
                    location={loc}
                    isCurrent={locationGuard.currentLocation?.locationHash === loc.locationHash}
                    onRelease={locationGuard.releaseLocation}
                    onRename={locationGuard.renameLocation}
                    onMarkHome={locationGuard.markHome}
                  />
                ))}
              </div>
            </>
          )}

          {/* ── ACCOUNT ── */}
          <SectionHeader>Account</SectionHeader>

          <div style={{
            padding: 16, borderRadius: 14,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>Signed in as</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
              {auth?.user?.email || 'Unknown'}
            </div>
            <button
              onClick={handleSignOut}
              style={{
                width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)', color: '#ef4444',
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              Sign Out
            </button>
          </div>

          <PublicNameSection />

          <ChangePasswordSection auth={auth} />

          {/* ── UNINSTALL (Connector only) ── */}
          {isTauriApp && (
            <div style={{
              marginTop: 16, padding: 16, borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border-light)',
            }}>
              {!showUninstallConfirm && !uninstallDone && (
                <button
                  onClick={() => setShowUninstallConfirm(true)}
                  style={{
                    width: '100%', fontSize: 13, fontWeight: 600, padding: '10px 0', borderRadius: 10,
                    background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                >
                  Uninstall Maestro
                </button>
              )}

              {showUninstallConfirm && !uninstallDone && (
                <div style={{
                  padding: 14, borderRadius: 12,
                  background: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.15)',
                }}>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
                    This will remove all Maestro data from your Mac, including the background service
                    and cached files. Your account and subscription are not affected. Are you sure?
                  </p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={handleFullUninstall}
                      disabled={uninstallLoading}
                      style={{
                        flex: 1, fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
                        background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                        cursor: uninstallLoading ? 'wait' : 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      {uninstallLoading ? 'Removing…' : 'Yes, Uninstall'}
                    </button>
                    <button
                      onClick={() => setShowUninstallConfirm(false)}
                      style={{
                        flex: 1, fontSize: 12, fontWeight: 600, padding: '9px 0', borderRadius: 8,
                        background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
                        cursor: 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      No, Keep It
                    </button>
                  </div>
                </div>
              )}

              {uninstallDone && (
                <div style={{
                  padding: 14, borderRadius: 12,
                  background: 'rgba(34,197,94,0.06)',
                  border: '1px solid rgba(34,197,94,0.15)',
                  textAlign: 'center',
                }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#22c55e', marginBottom: 4 }}>
                    Maestro has been removed.
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    Drag Maestro.app from your Applications folder to the Trash to finish.
                    This window will close shortly.
                  </p>
                </div>
              )}
            </div>
          )}

          {showDeveloperSettings && (
            <>
              <SectionHeader>Developer</SectionHeader>
              <div style={{
                padding: 16, borderRadius: 14,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                marginBottom: 6,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 12,
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-light)',
                }}>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                      Local Core Library (Dev)
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.4 }}>
                      Route local library catalog calls through embedded local core
                    </div>
                  </div>
                  <PillToggle checked={localCoreEnabled} onChange={handleLocalCoreToggle} />
                </div>
              </div>
            </>
          )}

          {/* ── VERSION + UPDATE CHECK ── */}
          {appVersion && (
            <div style={{
              textAlign: 'center',
              paddingTop: 24,
              paddingBottom: 32,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)', letterSpacing: '0.02em' }}>
                Maestro v{appVersion}{appBuildDate ? ` · ${appBuildDate}` : ''}
              </div>

              <button
                type="button"
                onClick={handleUpdateButtonClick}
                disabled={updateStatus === 'checking'}
                aria-live="polite"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  marginTop: 14, padding: '12px 28px', borderRadius: 12,
                  fontSize: 14, fontWeight: 700, letterSpacing: '0.01em',
                  cursor: updateStatus === 'checking' ? 'wait' : 'pointer',
                  transition: 'all 0.2s',
                  background: updateStatus === 'update-available'
                    ? 'linear-gradient(135deg, #C9A84C 0%, #d4b65e 100%)'
                    : updateStatus === 'up-to-date'
                      ? 'rgba(34,197,94,0.12)'
                      : (updateStatus === 'check-failed' || updateStatus === 'error')
                        ? 'rgba(248, 113, 113, 0.12)'
                        : 'var(--bg-secondary, rgba(255,255,255,0.06))',
                  color: updateStatus === 'update-available'
                    ? '#1a1a2e'
                    : updateStatus === 'up-to-date'
                      ? '#22c55e'
                      : (updateStatus === 'check-failed' || updateStatus === 'error')
                        ? '#f87171'
                        : 'var(--text-secondary)',
                  border: updateStatus === 'up-to-date'
                    ? '1px solid rgba(34,197,94,0.25)'
                    : (updateStatus === 'check-failed' || updateStatus === 'error')
                      ? '1px solid rgba(248, 113, 113, 0.35)'
                      : '1px solid var(--border-light, rgba(255,255,255,0.08))',
                  opacity: updateStatus === 'checking' ? 0.7 : 1,
                }}
              >
                {updateStatus === 'checking' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                {updateStatus === 'up-to-date' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {updateStatus === 'update-available' && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                )}
                {(updateStatus === 'check-failed' || updateStatus === 'error') && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                )}
                {updateStatus === 'checking' ? 'Checking…'
                  : updateStatus === 'up-to-date' ? `You're on the latest version (v${appVersion})`
                  : updateStatus === 'check-failed' ? 'Try Again'
                  : updateStatus === 'update-available' ? `v${latestVersion || 'latest'} available — Download`
                  : updateStatus === 'error' ? 'Try Again'
                  : 'Check for Updates'}
              </button>

              {(updateStatus === 'check-failed' || updateStatus === 'error') && updateDetailMessage && (
                <p
                  role="alert"
                  style={{
                    marginTop: 10,
                    marginBottom: 0,
                    fontSize: 12,
                    lineHeight: 1.45,
                    color: '#f87171',
                    maxWidth: 320,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  {updateDetailMessage}
                </p>
              )}

              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', opacity: 0.6, marginTop: 12, lineHeight: 1.5 }}>
                {t('legal.copyright')}
                <br />
                {t('legal.product_of')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', opacity: 0.75, marginTop: 14, lineHeight: 1.5, textAlign: 'left' }}>
                <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{t('settings.legal_section')}</div>
                <div>{t('settings.legal_body')}</div>
                <div style={{ marginTop: 4 }}>{t('settings.legal_address')}</div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <a href="/privacy" style={{ color: GOLD, textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); window.open('/privacy', '_blank'); }}>Privacy</a>
                  <a href="/terms" style={{ color: GOLD, textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); window.open('/terms', '_blank'); }}>Terms</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <FullFeaturedModal isOpen={showFullFeaturedModal} onClose={() => {
        setShowFullFeaturedModal(false);
        setDesktopMode('standard');
      }} />
    </div>
  );
}
